import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Languages, Moon, Sun } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { setLanguage } from "@/i18n";
import { formatApiError } from "@/api/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import LogoMonogram from "@/components/LogoMonogram";

const BG_IMAGE = "/login-bg.jpg";

export default function Login() {
  const { t, i18n } = useTranslation();
  const { user, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pick the first route the user actually has permission for.
  const routeForUser = (u) => {
    if (!u) return "/dashboard";
    const has = (p) =>
      u.role === "admin" ||
      (u.permissions || []).includes("*") ||
      (u.permissions || []).includes(p);
    const candidates = [
      ["report:kpis", "/dashboard"],
      ["report:ganador", "/reports/ganador"],
      ["report:ranking", "/reports/ranking"],
      ["report:tendencia", "/reports/tendencia"],
      ["report:distribucion", "/reports/distribucion"],
      ["report:heatmap", "/reports/heatmap"],
      ["report:tabla", "/reports/tabla"],
      ["report:3d", "/reports/3d"],
      ["report:comparativo", "/reports/comparativo"],
      ["upload", "/upload"],
      ["users:manage", "/admin/users"],
      ["dept:manage", "/admin/departments"],
    ];
    for (const [perm, path] of candidates) {
      if (has(perm)) return path;
    }
    return "/dashboard";
  };

  useEffect(() => {
    if (user && user !== false) {
      const dest = location.state?.from?.pathname || routeForUser(user);
      navigate(dest, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, location.state]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("expired")) {
      setError(t("login.expired"));
    }
  }, [location.search, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError(t("login.errors.required"));
      return;
    }
    setSubmitting(true);
    try {
      const loggedInUser = await login(email.trim().toLowerCase(), password);
      const dest = location.state?.from?.pathname || routeForUser(loggedInUser);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4 py-12 text-foreground"
      data-testid="login-page"
      style={{
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Dark overlay sits above the bg image so the form keeps WCAG contrast
          regardless of how the warehouse photo crops on different viewports. */}
      <div
        className="absolute inset-0 bg-slate-950/65"
        aria-hidden="true"
        data-testid="login-overlay"
      />

      <Toaster richColors position="top-right" />

      <div className="absolute right-4 top-4 z-10 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setLanguage(i18n.language === "es" ? "en" : "es")}
          data-testid="login-lang-toggle"
          className="gap-2 border-slate-700/70 bg-slate-900/70 text-slate-100 hover:bg-slate-800/80 hover:text-white"
        >
          <Languages className="h-4 w-4" />
          <span className="font-mono text-xs uppercase">{i18n.language}</span>
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={toggle}
          data-testid="login-theme-toggle"
          className="border-slate-700/70 bg-slate-900/70 text-slate-100 hover:bg-slate-800/80 hover:text-white"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
      </div>

      <Card
        className="relative z-10 w-full max-w-md border-slate-700/60 bg-slate-900/85 text-slate-100 shadow-2xl backdrop-blur-md"
        data-testid="login-card"
      >
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto">
            <LogoMonogram size={56} testId="login-logo" />
          </div>
          <CardTitle
            className="text-center text-2xl font-semibold tracking-tight"
            data-testid="login-app-name"
          >
            {t("app.name")}
          </CardTitle>
          <CardDescription className="text-center text-slate-400">
            {t("login.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-slate-200">
                {t("login.email")}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bodega.com"
                data-testid="login-email-input"
                className="border-slate-700 bg-slate-950/70 text-slate-100 placeholder:text-slate-500"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-slate-200">
                {t("login.password")}
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="border-slate-700 bg-slate-950/70 text-slate-100 placeholder:text-slate-500"
                required
              />
            </div>
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="login-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("login.loading")}
                </>
              ) : (
                t("login.submit")
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
